// /billing: the company's products and add-ons (common/slots.py has the rules;
// API: 100-api functions/billing).
//
//   LeadFlow (either or both)
//     LeadFlow ASHP    £99/month   up to 3 heat pump estimate components
//     LeadFlow Solar   £99/month   up to 3 solar estimate components
//       each with an option: single page hosting, +£15/month (one hosted page
//       for that product)
//   QuoteFlow (the upgrade)
//                      £219/month  both LeadFlows and both hostings plus the
//                                  quotation flow
//   + Contact forms, £5/month (up to 3); a switch, with at least one product
//
// The API takes plan = "estimate" (LeadFlow ASHP) | "quoteflow" | "", solar on/off and
// hosting / solarHosting 0|1; with QuoteFlow, solar and both hostings are included
// rather than separate lines.
//
// Yearly is 10 × monthly. The prices shown come from the API (Stripe), not from here.
//
// Choose the products (and the add-ons), then Checkout (first time: Stripe Checkout,
// monthly or yearly) or Update (changes the subscription now; Stripe charges or
// credits the difference). "Manage billing" (Stripe portal) once there's been a
// purchase. Building and testing on our demo page is free.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, Check, CheckCircle2, CreditCard, ShoppingCart } from "lucide-react";
import { useApi, type BillingLine, type BillingOverview, type BillingPlan, type BillingPriceKey } from "../api";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ProductIcon } from "../components/ProductIcon";
import "../widgets.css";
import { track } from "../track";

type Interval = "monthly" | "yearly";

// The products as the page shows them (prices come from Stripe)
type ProductKey = "ashp" | "solar" | "quoteflow";
const PRODUCTS: Record<ProductKey, { name: string; tagline: string; includes: string[] }> = {
  ashp: {
    name: "LeadFlow ASHP",
    tagline: "Win leads with instant heat pump estimates on your website.",
    includes: [
      "Up to 3 heat pump estimate components (Compact, Full width or floating)",
      "Sized from the home's EPC (RdSAP) and priced from your list",
      "Callback requests, Leads and analytics",
    ],
  },
  solar: {
    name: "LeadFlow Solar",
    tagline: "Win leads with instant solar panel estimates on your website.",
    includes: [
      "Up to 3 solar estimate components (Full width)",
      "Panels shown on the home's own roof, priced from your prices",
      "Callback requests, Leads and analytics",
    ],
  },
  quoteflow: {
    name: "QuoteFlow",
    tagline: "Turn your leads into quotes your customers accept online.",
    includes: [
      "LeadFlow ASHP and LeadFlow Solar included",
      "Single page hosting for both included",
      "Your catalogue, packages and margins",
      "Quotes built in minutes, accepted online",
      "Instant detailed quotes from your website",
      "Heat pump (ASHP) quotes today; solar quotes planned for November 2026",
    ],
  },
};
const PLAN_NAMES: Record<BillingPlan, string> = { estimate: "LeadFlow ASHP", quoteflow: "QuoteFlow" };

// Pounds: whole pounds as "£99", anything with pence as "£49.50"
const gbp = (pence?: number) =>
  pence === undefined
    ? "…"
    : `£${(pence / 100).toLocaleString("en-GB", pence % 100 === 0 ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (epoch: number | null) => (epoch ? new Date(epoch * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "");
const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
// "a", "a and b", "a, b and c"
const andList = (items: string[]) => (items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);

export function BillingPage() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<BillingOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The basket: LeadFlow ASHP and LeadFlow Solar on/off, each with its own single
  // page hosting option, QuoteFlow on/off (it includes both LeadFlows and their
  // hosting), and the contact forms add-on
  const [ashp, setAshp] = useState(false);
  const [solar, setSolar] = useState(false);
  const [quoteflow, setQuoteflow] = useState(false);
  const [hosting, setHosting] = useState(0);
  const [solarHosting, setSolarHosting] = useState(0);
  const [contactForms, setContactForms] = useState(false);
  const [newInterval, setNewInterval] = useState<Interval>("monthly");
  const [confirming, setConfirming] = useState<null | "update" | "cancel">(null);
  const returned = params.get("checkout");

  // Start the basket at what the subscription has (nothing before a purchase)
  const fromOverview = useCallback((d: BillingOverview) => {
    setData(d);
    // QuoteFlow includes both LeadFlows, so leaving it starts from both switched on
    setQuoteflow(d.plan === "quoteflow");
    setAshp(d.plan !== "");
    setSolar(Boolean(d.solar) || d.plan === "quoteflow");
    // ...and the same for its hosting, so leaving QuoteFlow keeps the hosted pages
    setHosting(d.plan === "quoteflow" ? 1 : d.hosting);
    setSolarHosting(d.plan === "quoteflow" ? 1 : (d.solarHosting ?? 0));
    setContactForms(d.contactForms);
  }, []);
  const load = useCallback(() => api.getBilling().then(fromOverview), [api, fromOverview]);
  useEffect(() => {
    load().catch((e) => setError((e as Error).message));
  }, [load]);

  // Back from Checkout: re-check every 2s (up to ~30s) until the webhook has confirmed it
  const waiting = returned === "success" && !!data && !data.active;
  useEffect(() => {
    if (!waiting) return;
    let tries = 0;
    const t = window.setInterval(() => {
      tries += 1;
      void load().catch(() => undefined);
      if (tries >= 15) window.clearInterval(t);
    }, 2000);
    return () => window.clearInterval(t);
  }, [waiting, load]);

  // Back from Checkout and the webhook has confirmed it: report the purchase once per
  // subscription (a refresh keeps ?checkout=success, so remember it in localStorage).
  // Value = the subscription's lines at their Stripe prices, in pounds.
  const confirmedSub = returned === "success" && data?.active ? data.subscription : null;
  useEffect(() => {
    if (!confirmedSub || !data?.prices) return;
    const key = `xq_purchase_${confirmedSub.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch {
      // storage blocked: still report it, a duplicate is better than none
    }
    // Old per-component lines have no price here, so they count as 0
    const pence = (Object.entries(confirmedSub.quantities) as [BillingLine, number][]).reduce((sum, [line, qty]) => {
      const each = data.prices?.[line as BillingPriceKey]?.[confirmedSub.interval] ?? 0;
      return sum + each * (qty ?? 0);
    }, 0);
    // No transaction_id: the subscription id identifies the company (track.ts: no ids)
    track("purchase", { currency: "GBP", value: pence / 100, plan: data.plan, solar: Boolean(data.solar), interval: confirmedSub.interval });
  }, [confirmedSub, data]);

  const active = !!data?.active && !!data.subscription;
  // An existing subscription keeps its own period; a new one is chosen here
  const period: Interval = active ? data!.subscription!.interval : newInterval;
  const per = period === "yearly" ? "year" : "month";
  const price = (key: BillingPriceKey) => data?.prices?.[key]?.[period];

  // What the API is sent: QuoteFlow, else LeadFlow ASHP ("estimate") or none, and
  // LeadFlow Solar only when it's a line of its own (QuoteFlow includes it)
  const plan: BillingPlan | "" = quoteflow ? "quoteflow" : ashp ? "estimate" : "";
  const solarLine = !quoteflow && solar;

  // Hosting is bought per LeadFlow (none with QuoteFlow, which includes both)
  const effectiveHosting = !quoteflow && ashp ? hosting : 0;
  const effectiveSolarHosting = !quoteflow && solar ? solarHosting : 0;
  // Contact forms need at least one product
  const hasProduct = quoteflow || ashp || solar;
  const effectiveForms = hasProduct && contactForms;

  // The basket, line by line (the total card lists them)
  const lines: { label: string; pence: number; note?: string }[] = [];
  if (quoteflow) lines.push({ label: "QuoteFlow", pence: price("quoteflow") ?? 0, note: "Includes LeadFlow ASHP and LeadFlow Solar" });
  else {
    if (ashp) lines.push({ label: "LeadFlow ASHP", pence: price("estimate") ?? 0 });
    if (effectiveHosting) lines.push({ label: "Single page hosting (ASHP)", pence: (price("hosting") ?? 0) * effectiveHosting });
    if (solar) lines.push({ label: "LeadFlow Solar", pence: price("solar") ?? 0 });
    if (effectiveSolarHosting) lines.push({ label: "Single page hosting (Solar)", pence: (price("solarhosting") ?? 0) * effectiveSolarHosting });
  }
  if (effectiveForms) lines.push({ label: "Contact forms", pence: price("contactforms") ?? 0 });
  const total = lines.reduce((sum, l) => sum + l.pence, 0);

  const changed =
    !!data &&
    (plan !== data.plan ||
      solarLine !== Boolean(data.solar) ||
      effectiveHosting !== data.hosting ||
      effectiveSolarHosting !== (data.solarHosting ?? 0) ||
      effectiveForms !== data.contactForms);
  // What's chosen, in words (the total and the confirm dialog)
  const summary = lines.map((l) => l.label).join(" + ");
  // What the subscription has now, in words (the current-plan banner)
  const currentProducts = data ? [data.plan ? PLAN_NAMES[data.plan] : "", data.solar ? "LeadFlow Solar" : ""].filter(Boolean) : [];
  // Is a product on the subscription now? (QuoteFlow covers both LeadFlows)
  const isCurrent = (p: ProductKey) =>
    active && !!data && (p === "quoteflow" ? data.plan === "quoteflow" : data.plan !== "quoteflow" && (p === "ashp" ? data.plan === "estimate" : Boolean(data.solar)));
  const currentAddons = data
    ? [data.hosting ? "heat pump page hosting" : "", data.solarHosting ? "solar page hosting" : "", data.contactForms ? "contact forms" : ""].filter(Boolean)
    : [];
  // Components that aren't live (an API without a category yet counts as none)
  const notLive = data ? Object.values(data.components).reduce((n, items) => n + (items ?? []).filter((t) => !t.covered).length, 0) : 0;

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }
  const checkout = () =>
    run(async () => {
      if (!hasProduct) return;
      track("begin_checkout", { currency: "GBP", plan, solar: solarLine });
      window.location.href = await api.startCheckout(period, plan, solarLine, effectiveHosting, effectiveSolarHosting, effectiveForms);
    });
  const manage = () =>
    run(async () => {
      window.location.href = await api.openBillingPortal();
    });
  // Change the subscription now; plan "" with solar off ends it at the period's close
  const update = (to: BillingPlan | "", withSolar: boolean, host: number, solarHost: number, forms: boolean) =>
    run(async () => {
      fromOverview(await api.updatePlan(to, withSolar, host, solarHost, forms));
      setConfirming(null);
      setBusy(false);
    });

  return (
    <div className="page billing">
      <div className="page-head billing-head">
        <div>
          <h1>Billing</h1>
          <p>Choose LeadFlow ASHP, LeadFlow Solar or both, or QuoteFlow for both plus quoting. Add contact forms and single page hosting if you want them. Building and testing is free.</p>
        </div>
        {data && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void manage()}
            disabled={busy || !data.hasCustomer}
            title={data.hasCustomer ? "Cards, invoices, VAT details and cancelling" : "Available after your first purchase"}
          >
            <CreditCard size={16} aria-hidden="true" /> Manage billing
          </button>
        )}
      </div>

      {returned === "success" && (
        <div className="flash flash-success">
          {waiting ? "Thanks! Confirming your payment with Stripe…" : "Thanks! You're all set."}{" "}
          <button type="button" className="link-btn" onClick={() => setParams({})}>
            Dismiss
          </button>
        </div>
      )}
      {returned === "cancelled" && <div className="flash flash-info">Checkout cancelled: nothing was charged.</div>}
      {error && <div className="flash flash-danger">{error}</div>}
      {!data && !error && <p className="muted">Loading…</p>}

      {data && (
        <>
          {/* Where they are now */}
          {active ? (
            <div className="flash flash-info billing-current">
              <CheckCircle2 size={16} aria-hidden="true" />
              <span>
                {currentProducts.length ? (
                  <>
                    You have <strong>{andList(currentProducts)}</strong>
                    {currentAddons.length ? <> with {andList(currentAddons)}</> : null}, billed {data.subscription!.interval}.
                  </>
                ) : (
                  <>You're on the per-component billing from before the plans.</>
                )}{" "}
                {data.subscription!.cancelAtPeriodEnd
                  ? `It ends on ${date(data.subscription!.currentPeriodEnd)}.`
                  : `Renews on ${date(data.subscription!.currentPeriodEnd)}.`}
                {data.legacy && !currentProducts.length ? " Choosing a product below replaces it." : ""}
              </span>
            </div>
          ) : (
            <div className="billing-interval">
              <div className="ed-segmented" role="radiogroup" aria-label="Billing period">
                {(["monthly", "yearly"] as Interval[]).map((i) => (
                  <button key={i} type="button" role="radio" aria-checked={newInterval === i} className={newInterval === i ? "is-on" : ""} onClick={() => setNewInterval(i)}>
                    {i === "monthly" ? "Monthly" : "Yearly (2 months free)"}
                  </button>
                ))}
              </div>
            </div>
          )}

          {notLive > 0 && (
            <div className="flash flash-danger">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>
                {count(notLive, "component")} {notLive === 1 ? "isn't" : "aren't"} live because {notLive === 1 ? "it isn't" : "they aren't"} covered by your subscription. They still work on the test page. <Link to="/components">See your components</Link>
              </span>
            </div>
          )}

          {/* LeadFlow: ASHP and Solar side by side, either or both */}
          <h2 className="billing-section-title">LeadFlow</h2>
          <div className="billing-plans">
            {(["ashp", "solar"] as const).map((p) => {
              const on = p === "ashp" ? ashp : solar;
              const set = p === "ashp" ? setAshp : setSolar;
              const fullPrice = price(p === "ashp" ? "estimate" : "solar");
              // This LeadFlow's single page hosting option
              const hostOn = p === "ashp" ? effectiveHosting > 0 : effectiveSolarHosting > 0;
              const setHost = p === "ashp" ? setHosting : setSolarHosting;
              const option = (
                <HostingOption
                  label={p === "ashp" ? "heat pump" : "solar"}
                  price={`+${gbp(price(p === "ashp" ? "hosting" : "solarhosting"))}/${per}`}
                  included={quoteflow}
                  on={hostOn}
                  disabled={!on}
                  onToggle={(v) => setHost(v ? 1 : 0)}
                />
              );
              return (
                <ProductCard key={p} product={p} on={quoteflow || on} current={isCurrent(p)} included={quoteflow} onToggle={quoteflow ? undefined : set} option={option}>
                  {quoteflow ? (
                    <span className="billing-plan-price billing-included">Included in QuoteFlow</span>
                  ) : (
                    <span className="billing-plan-price">
                      {gbp(fullPrice)}
                      <small>/{per} + VAT</small>
                    </span>
                  )}
                </ProductCard>
              );
            })}
          </div>

          {/* QuoteFlow: the upgrade, covering both LeadFlows plus quoting */}
          <h2 className="billing-section-title">Upgrade</h2>
          <ProductCard product="quoteflow" on={quoteflow} current={isCurrent("quoteflow")} featured onToggle={setQuoteflow}>
            <span className="billing-plan-price">
              {gbp(price("quoteflow"))}
              <small>/{per} + VAT</small>
            </span>
          </ProductCard>

          {/* The add-on (with any product) */}
          <h2 className="billing-section-title">Add-ons</h2>
          <section className={`card billing-addon-row${hasProduct ? "" : " is-off"}`}>
            <div className="billing-addon-text">
              <ProductIcon product="contact" size={28} />
              <div>
                <h2>Contact forms</h2>
                <p className="card-sub">
                  Up to 3 contact forms on your website, emailed straight to you. {gbp(price("contactforms"))} a {per}.
                </p>
                {!hasProduct && <p className="muted">Choose a LeadFlow or QuoteFlow to add contact forms.</p>}
              </div>
            </div>
            <label className="ed-toggle">
              <input type="checkbox" role="switch" aria-label="Add contact forms" checked={effectiveForms} disabled={!hasProduct} onChange={(e) => setContactForms(e.target.checked)} />
              <span className="ed-toggle-track" aria-hidden="true" />
            </label>
          </section>

          {/* The basket: a cart heading, each line, then the total and the one action on the bottom row */}
          <section className="card billing-total">
            <h2 className="billing-total-title">
              <ShoppingCart size={18} aria-hidden="true" /> Your basket
            </h2>
            {lines.length ? (
              <ul className="billing-lines">
                {lines.map((l) => (
                  <li key={l.label}>
                    <span>
                      {l.label}
                      {l.note && <span className="billing-line-note">{l.note}</span>}
                    </span>
                    <span>{gbp(l.pence)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="billing-total-label">Choose LeadFlow ASHP, LeadFlow Solar or QuoteFlow</div>
            )}
            <div className="billing-total-foot">
              <div className="billing-total-price">
                {gbp(total)} <small>/{per} + VAT</small>
              </div>
              <div className="billing-total-actions">
                {!active ? (
                  <button type="button" className="btn btn-primary" disabled={busy || !hasProduct} onClick={() => void checkout()}>
                    {busy ? "Opening checkout…" : "Checkout"}
                  </button>
                ) : (
                  <>
                    {currentProducts.length > 0 && !data.subscription!.cancelAtPeriodEnd && (
                      <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setConfirming("cancel")}>
                        Cancel plan
                      </button>
                    )}
                    <button type="button" className="btn btn-primary" disabled={busy || !hasProduct || (!changed && !data.subscription!.cancelAtPeriodEnd)} onClick={() => setConfirming("update")}>
                      {data.subscription!.cancelAtPeriodEnd && !changed ? "Keep my plan" : "Update plan"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Changes to an active subscription take effect now, so confirm first */}
      <ConfirmDialog
        open={confirming !== null}
        title={confirming === "cancel" ? "Cancel your plan?" : hasProduct ? `Change to ${summary} for ${gbp(total)} a ${per}?` : "Change your plan?"}
        confirmLabel={confirming === "cancel" ? "Cancel plan" : "Update plan"}
        busy={busy}
        danger={confirming === "cancel"}
        onConfirm={() => void (confirming === "cancel" ? update("", false, 0, 0, false) : update(plan, solarLine, effectiveHosting, effectiveSolarHosting, effectiveForms))}
        onCancel={() => setConfirming(null)}
      >
        <p>
          {confirming === "cancel"
            ? "Everything stays live until the end of this billing period, then stops. You can come back any time."
            : "The change applies now: you're charged (or credited) the difference for the rest of this billing period, then the new price each renewal."}
        </p>
      </ConfirmDialog>
    </div>
  );
}

/**
 * One product's card: name and icon, an on/off switch (none while it's included
 * in QuoteFlow), the price (children), tagline and what's included.
 */
function ProductCard({
  product,
  on,
  current,
  included = false,
  featured = false,
  onToggle,
  option,
  children,
}: {
  product: ProductKey;
  on: boolean;
  current: boolean;
  included?: boolean;
  featured?: boolean;
  onToggle?: (on: boolean) => void;
  option?: ReactNode;
  children: ReactNode;
}) {
  const p = PRODUCTS[product];
  return (
    <section className={`card billing-plan billing-product${on ? " is-on" : ""}${included ? " is-included" : ""}${featured ? " is-featured" : ""}`}>
      <span className="billing-plan-head">
        <span className="billing-plan-name">
          {/* QuoteFlow covers both products, so it shows both icons */}
          {product === "quoteflow" ? (
            <>
              <ProductIcon product="ashp" size={28} />
              <ProductIcon product="solar" size={28} />
            </>
          ) : (
            <ProductIcon product={product} size={28} />
          )}{" "}
          {p.name}
        </span>
        {onToggle && (
          <label className="ed-toggle">
            <input type="checkbox" role="switch" aria-label={p.name} checked={on} onChange={(e) => onToggle(e.target.checked)} />
            <span className="ed-toggle-track" aria-hidden="true" />
          </label>
        )}
      </span>
      {current && <span className="status-chip status-live billing-chip">On your plan</span>}
      {children}
      <span className="billing-plan-tagline">{p.tagline}</span>
      <span className="billing-plan-list">
        {p.includes.map((i) => (
          <span key={i}>
            <Check size={14} aria-hidden="true" /> {i}
          </span>
        ))}
      </span>
      {option}
    </section>
  );
}

/**
 * A LeadFlow's single page hosting option, at the foot of its card: a switch
 * with the price, or "Included" with QuoteFlow. Off (greyed) until the LeadFlow is on.
 */
function HostingOption({
  label,
  price,
  included,
  on,
  disabled,
  onToggle,
}: {
  label: string;
  price: string;
  included: boolean;
  on: boolean;
  disabled: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <div className={`billing-option${disabled && !included ? " is-off" : ""}`}>
      <div className="billing-option-text">
        <span className="billing-option-name">
          Single page hosting <span className="billing-option-price">{included ? "Included" : price}</span>
        </span>
        <span className="billing-option-sub">Your {label} estimator on a page we host at go.xenquote.com, with your header, footer and QR code.</span>
      </div>
      {included ? (
        <Check size={18} className="billing-option-check" aria-label="Included in QuoteFlow" />
      ) : (
        <label className="ed-toggle">
          <input type="checkbox" role="switch" aria-label={`Single page hosting for your ${label} estimator`} checked={on} disabled={disabled} onChange={(e) => onToggle(e.target.checked)} />
          <span className="ed-toggle-track" aria-hidden="true" />
        </label>
      )}
    </div>
  );
}
